import type { DocumentMeta, FolderMeta, Library } from "@quill/shared";

/** Natural order, so "Chapter 2" sorts before "Chapter 10". */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
export const byName = (a: string, b: string) => collator.compare(a, b);

export class LibraryView {
  readonly folders: Map<string, FolderMeta>;

  constructor(readonly data: Library) {
    this.folders = new Map(data.folders.map((f) => [f.id, f]));
  }

  /** True if the folder or an ancestor is in Trash. */
  folderTrashed(id: string | null): boolean {
    for (let cur = id ? this.folders.get(id) : undefined, i = 0; cur && i < 1000; i++) {
      if (cur.deletedAt) return true;
      cur = cur.parentId ? this.folders.get(cur.parentId) : undefined;
    }
    return false;
  }

  docTrashed(d: DocumentMeta): boolean {
    return d.deletedAt !== null || this.folderTrashed(d.folderId);
  }

  /** Live contents of a folder (null = top level). */
  children(folderId: string | null): { folders: FolderMeta[]; documents: DocumentMeta[] } {
    return {
      folders: this.data.folders
        .filter((f) => f.parentId === folderId && !f.deletedAt)
        .sort((a, b) => byName(a.name, b.name)),
      documents: this.data.documents
        .filter((d) => d.folderId === folderId && !d.deletedAt)
        .sort((a, b) => byName(a.title, b.title)),
    };
  }

  /** Folder chain from the top level down to `id`. */
  path(id: string | null): FolderMeta[] {
    const out: FolderMeta[] = [];
    for (let cur = id ? this.folders.get(id) : undefined, i = 0; cur && i < 1000; i++) {
      out.unshift(cur);
      cur = cur.parentId ? this.folders.get(cur.parentId) : undefined;
    }
    return out;
  }

  /** What Trash shows: trashed items whose container isn't itself in Trash. */
  trashTopLevel(): { folders: FolderMeta[]; documents: DocumentMeta[] } {
    return {
      folders: this.data.folders
        .filter((f) => f.deletedAt && !this.folderTrashed(f.parentId))
        .sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0)),
      documents: this.data.documents
        .filter((d) => d.deletedAt && !this.folderTrashed(d.folderId))
        .sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0)),
    };
  }

  /** Number of items Trash shows (a trashed folder counts once). */
  trashCount(): number {
    const t = this.trashTopLevel();
    return t.folders.length + t.documents.length;
  }

  /** Every live folder as an indented list, for the "Move to…" picker. */
  folderTree(exclude?: string): { folder: FolderMeta; depth: number }[] {
    const out: { folder: FolderMeta; depth: number }[] = [];
    const walk = (parent: string | null, depth: number) => {
      for (const f of this.children(parent).folders) {
        if (f.id === exclude) continue; // a folder can't move into itself or its descendants
        out.push({ folder: f, depth });
        walk(f.id, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  }

  /** Words in every live document inside the folder, its subfolders included. */
  folderWords(id: string): number {
    const { folders, documents } = this.children(id);
    return documents.reduce((n, d) => n + (d.wordCount ?? 0), 0) + folders.reduce((n, f) => n + this.folderWords(f.id), 0);
  }

  /** The live document edited most recently, for "open where she left off". */
  lastEdited(): DocumentMeta | undefined {
    return this.data.documents
      .filter((d) => !this.docTrashed(d))
      .sort((a, b) => b.updatedAt - a.updatedAt)[0];
  }
}
